import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  SecurityContext,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-streaming-text',
  standalone: true,
  imports: [CommonModule],
  // The markdown HTML is injected via [innerHTML]; Angular's emulated
  // encapsulation does NOT add scope attributes to runtime-inserted nodes,
  // so component-scoped styles can't reach it. We use ViewEncapsulation.None
  // and scope EVERY rule under .app-streaming-md so nothing bleeds globally.
  encapsulation: ViewEncapsulation.None,
  template: '<div class="app-streaming-md" [innerHTML]="displayedContent"></div>',
  styles: [`
    .app-streaming-md { line-height: 1.5; word-break: break-word; }

    .app-streaming-md > *:first-child { margin-top: 0; }
    .app-streaming-md > *:last-child  { margin-bottom: 0; }

    .app-streaming-md p { margin: 0 0 0.55em; }

    .app-streaming-md h1,
    .app-streaming-md h2,
    .app-streaming-md h3,
    .app-streaming-md h4,
    .app-streaming-md h5,
    .app-streaming-md h6 {
      margin: 1em 0 0.4em;
      font-weight: 700;
      line-height: 1.25;
    }
    .app-streaming-md h1 { font-size: 1.25em; }
    .app-streaming-md h2 { font-size: 1.18em; }
    .app-streaming-md h3 { font-size: 1.1em; }
    .app-streaming-md h4,
    .app-streaming-md h5,
    .app-streaming-md h6 { font-size: 1em; }

    .app-streaming-md ul,
    .app-streaming-md ol {
      margin: 0.35em 0 0.6em;
      padding-left: 1.5em;
    }
    /* nested lists hug their parent item */
    .app-streaming-md li > ul,
    .app-streaming-md li > ol {
      margin: 0.2em 0 0.2em;
    }
    .app-streaming-md li { margin: 0.18em 0; }

    .app-streaming-md strong { font-weight: 600; }
    .app-streaming-md em { font-style: italic; }

    .app-streaming-md code {
      background: rgba(0, 0, 0, 0.06);
      padding: 0.1em 0.35em;
      border-radius: 4px;
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      font-size: 0.92em;
    }

    .app-streaming-md .md-table {
      border-collapse: collapse;
      margin: 0.7em 0;
      width: 100%;
      font-size: 0.92em;
      background: #fff;
    }
    .app-streaming-md .md-table th,
    .app-streaming-md .md-table td {
      border: 1px solid rgba(0, 0, 0, 0.18);
      padding: 6px 10px;
      text-align: left;
      vertical-align: top;
    }
    .app-streaming-md .md-table thead th {
      background: rgba(0, 0, 0, 0.06);
      font-weight: 600;
      white-space: nowrap;
    }
    .app-streaming-md .md-table tbody tr:nth-child(even) td {
      background: rgba(0, 0, 0, 0.025);
    }
  `],
})
export class StreamingTextComponent implements OnChanges {
  @Input() content: string = '';
  displayedContent: SafeHtml = '';
  private interval: any;

  constructor(private sanitizer: DomSanitizer) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['content']) {
      if (this.interval) {
        clearInterval(this.interval);
      }

      const newContent = changes['content'].currentValue || '';
      // Sanitize the full HTML content once
      const sanitizedContent = this.sanitizer.sanitize(SecurityContext.HTML, newContent);

      // Split into words for streaming effect. This is a simplification and might not perfectly handle all HTML.
      const words = (sanitizedContent || '').split(' ');
      let displayedWords: string[] = [];
      let i = 0;

      this.interval = setInterval(() => {
        if (i < words.length) {
          displayedWords.push(words[i]);
          this.displayedContent = this.sanitizer.bypassSecurityTrustHtml(displayedWords.join(' '));
          i++;
        } else {
          clearInterval(this.interval);
        }
      }, 50); // 50ms between words
    }
  }
}
